'use client';

import type React from 'react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import soundCloudData from '../data/soundCloudData';
import type { SoundCloudTrack } from '../types';

export interface PlayerContextValue {
  /** URL of the currently loaded SoundCloud track/set */
  currentTrackUrl: string | null;
  /** Title shown in the player bar */
  currentTrackTitle: string | null;
  /** Index within a playlist set if applicable */
  currentTrackIndex: number | null;
  /** Whether the player is currently playing */
  isPlaying: boolean;
  /** Whether auto-play on scroll is enabled */
  isAutoPlayEnabled: boolean;
  /** Whether the player bar is visible (a track has been loaded) */
  isVisible: boolean;
  /** Whether the player bar is collapsed to the mini button */
  isMinimized: boolean;
  /** Playlist queue of available tracks */
  queue: SoundCloudTrack[];
  /** Load and play a track by its SoundCloud URL with optional track index */
  playTrack: (url: string, title?: string, trackIndex?: number) => void;
  /** Skip to a track index in current playlist */
  skipToTrack: (trackIndex: number, title?: string) => void;
  /** Play the next track in the queue */
  playNext: () => void;
  /** Play the previous track in the queue */
  playPrev: () => void;
  /** Set the playlist queue */
  setQueue: (tracks: SoundCloudTrack[]) => void;
  /** Toggle play / pause */
  togglePlay: () => void;
  /** Toggle auto-play on scroll */
  toggleAutoPlay: () => void;
  /** Collapse the player bar to the mini button without pausing playback */
  hidePlayer: () => void;
  /** Expand the player bar from the mini button */
  expandPlayer: () => void;
  /** Register the iframe element so the SoundCloudPlayer can drive the widget */
  registerIframe: (iframe: HTMLIFrameElement | null) => void;
  /** Internal ref — used by SoundCloudPlayer to expose the SC widget */
  widgetRef: React.MutableRefObject<SCWidget | null>;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTrackUrl, setCurrentTrackUrl] = useState<string | null>(null);
  const [currentTrackTitle, setCurrentTrackTitle] = useState<string | null>(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [queue, setQueueState] = useState<SoundCloudTrack[]>(soundCloudData);

  const setQueue = useCallback((tracks: SoundCloudTrack[]) => {
    queueRef.current = tracks;
    setQueueState(tracks);
  }, []);

  const widgetRef = useRef<SCWidget | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Refs for values needed inside stable widget event callbacks
  const currentTrackUrlRef = useRef<string | null>(null);
  const queueRef = useRef<SoundCloudTrack[]>(soundCloudData);

  const registerIframe = useCallback((iframe: HTMLIFrameElement | null) => {
    iframeRef.current = iframe;
  }, []);

  const playTrack = useCallback((url: string, title?: string, trackIndex?: number) => {
    currentTrackUrlRef.current = url;
    setCurrentTrackUrl(url);
    setCurrentTrackTitle(title ?? null);
    setCurrentTrackIndex(trackIndex ?? null);
    setIsPlaying(true);
    setIsVisible(true);
    setIsMinimized(false);

    if (widgetRef.current && typeof trackIndex === 'number') {
      try {
        widgetRef.current.skip(trackIndex);
      } catch {
        // Widget will handle on reload
      }
    }
  }, []);

  const skipToTrack = useCallback((trackIndex: number, title?: string) => {
    setCurrentTrackIndex(trackIndex);
    if (title) setCurrentTrackTitle(title);
    setIsPlaying(true);
    setIsVisible(true);
    if (widgetRef.current) {
      widgetRef.current.skip(trackIndex);
      widgetRef.current.play();
    }
  }, []);

  const playNext = useCallback(() => {
    const q = queueRef.current;
    if (q.length === 0) return;
    // Use ref so this is always fresh even when called from a stale widget event callback
    const currentIndex = q.findIndex((t) => t.soundcloudUrl === currentTrackUrlRef.current);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % q.length : 0;
    const nextTrack = q[nextIndex];
    if (nextTrack) {
      playTrack(nextTrack.soundcloudUrl, nextTrack.title);
    }
  }, [playTrack]);

  const playPrev = useCallback(() => {
    const q = queueRef.current;
    if (q.length === 0) return;
    const currentIndex = q.findIndex((t) => t.soundcloudUrl === currentTrackUrlRef.current);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : q.length - 1;
    const prevTrack = q[prevIndex];
    if (prevTrack) {
      playTrack(prevTrack.soundcloudUrl, prevTrack.title);
    }
  }, [playTrack]);

  const togglePlay = useCallback(() => {
    if (widgetRef.current) {
      widgetRef.current.toggle();
    }
    setIsPlaying((prev) => !prev);
  }, []);

  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlayEnabled((prev) => !prev);
  }, []);

  const hidePlayer = useCallback(() => {
    // Keep playback running; only collapse UI to mini button
    setIsMinimized(true);
  }, []);

  const expandPlayer = useCallback(() => {
    setIsMinimized(false);
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        currentTrackUrl,
        currentTrackTitle,
        currentTrackIndex,
        isPlaying,
        isAutoPlayEnabled,
        isVisible,
        isMinimized,
        queue,
        playTrack,
        skipToTrack,
        playNext,
        playPrev,
        setQueue,
        togglePlay,
        toggleAutoPlay,
        hidePlayer,
        expandPlayer,
        registerIframe,
        widgetRef,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = (): PlayerContextValue => {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    throw new Error('usePlayer must be used inside <PlayerProvider>');
  }
  return ctx;
};

export default PlayerContext;
